import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "contacted", "closed"];
const STATUS_CLS = {
  new: "bg-amber-100 text-amber-800 border-amber-300",
  contacted: "bg-sky-100 text-sky-800 border-sky-300",
  closed: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

const inputCls =
  "w-full px-3.5 py-2.5 rounded-lg border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-700 bg-white text-stone-900";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [auth, setAuth] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch {
      toast.error("Could not load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAuth(data);
      } catch {
        setAuth(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (auth) loadEnquiries();
  }, [auth, loadEnquiries]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setAuth(data);
      toast.success("Welcome back!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAuth(false);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((en) => (en.id === id ? data : en)));
    } catch {
      toast.error("Could not update status");
    }
  };

  if (auth === null) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center" data-testid="admin-loading">
        <p className="text-stone-600 text-sm">Loading...</p>
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4" data-testid="admin-login-page">
        <form onSubmit={login} className="bg-white rounded-2xl border border-stone-200 shadow-lg p-8 w-full max-w-sm space-y-4" data-testid="admin-login-form">
          <div className="text-center space-y-1">
            <div className="w-12 h-12 rounded-full bg-amber-700 text-stone-50 flex items-center justify-center font-bold text-lg mx-auto">PT</div>
            <h1 className="text-xl font-bold text-stone-900 font-serif-display">Parth Travels Admin</h1>
            <p className="text-xs text-stone-600">Sign in to view trip enquiries</p>
          </div>
          <div>
            <label htmlFor="admin-email" className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1">Password</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p className="text-xs text-red-600 font-semibold" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-btn" className="w-full py-3 bg-amber-700 hover:bg-amber-800 text-white font-bold text-sm rounded-xl shadow-md transition-colors">
            Sign In
          </button>
          <a href="/" className="block text-center text-xs text-stone-600 hover:text-amber-800">← Back to website</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-dashboard">
      <header className="bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-700 text-stone-50 flex items-center justify-center font-bold text-sm">PT</div>
            <div>
              <span className="text-sm font-bold block leading-tight">Enquiry Dashboard</span>
              <span className="text-[11px] text-stone-400">{auth.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadEnquiries} data-testid="admin-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-800 hover:bg-stone-700 border border-stone-700 text-xs font-semibold transition-colors">
              <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-800 text-xs font-semibold transition-colors">
              <LogOut className="w-3.5 h-3.5" aria-hidden="true" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-stone-900" data-testid="enquiry-count">
            {enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"}
          </h2>
          <a href="/" className="text-xs text-stone-600 hover:text-amber-800">← Back to website</a>
        </div>

        {loading && enquiries.length === 0 ? (
          <p className="text-stone-600 text-sm">Loading enquiries...</p>
        ) : enquiries.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center" data-testid="enquiries-empty">
            <p className="text-stone-600 text-sm">No enquiries yet. New website enquiries will appear here instantly.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {enquiries.map((en) => (
              <div key={en.id} className="bg-white rounded-xl border border-stone-200 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`enquiry-row-${en.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-stone-900">{en.name}</span>
                    <span className="text-xs text-stone-500">{new Date(en.created_at).toLocaleString("en-IN")}</span>
                  </div>
                  <p className="text-xs text-stone-700 mt-1">
                    <a href={`tel:${en.phone.replace(/\s/g, "")}`} className="font-semibold text-amber-800 hover:underline">{en.phone}</a>
                    {en.email && <> · <a href={`mailto:${en.email}`} className="text-amber-800 hover:underline">{en.email}</a></>}
                  </p>
                  <p className="text-xs text-stone-600 mt-1">
                    <span className="font-semibold">{en.service}</span>
                    {en.details ? ` — ${en.details}` : ""}
                  </p>
                </div>
                <select
                  value={en.status}
                  onChange={(e) => setStatus(en.id, e.target.value)}
                  data-testid={`enquiry-status-${en.id}`}
                  className={`text-xs font-bold rounded-lg border px-2.5 py-1.5 capitalize focus:outline-none focus:ring-2 focus:ring-amber-700 ${STATUS_CLS[en.status] || STATUS_CLS.new}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s} className="capitalize">{s}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
